import { Octopus } from './index';

class OctopusManager {
  constructor() {
    this.instance = null;
    this.visibilityTimeout = null;
    this.hiddenSince = null;
    
    // Set up visibility change listener
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }
  
  // Check if authentication credentials exist
  isAuthValid() {
    const loginId = localStorage.getItem('login_id');
    const token = localStorage.getItem('auth_token');
    return !!(loginId && token);
  }

  // Handle visibility changes
  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // Guard: Don't wake up the socket if the user isn't logged in
      if (!this.isAuthValid()) {
        console.log('Tab visible but user is not logged in. Skipping connection.');
        return;
      }

      console.log('Tab became visible, checking connection...');
      
      // Clear any pending timeout
      if (this.visibilityTimeout) {
        clearTimeout(this.visibilityTimeout);
        this.visibilityTimeout = null;
      }
      
      // Check if we need to reconnect
      if (!this.isConnected()) {
        console.log('Connection not active, reconnecting...');
        this.connect();
      } else {
        // Even if connected, refresh subscriptions to get latest data
        console.log('Connection active, refreshing subscriptions...');
        this.refreshSubscriptions();
      }
    } else {
      // Tab became hidden
      console.log('Tab hidden, scheduling connection cleanup');
      this.hiddenSince = Date.now();
      
      // Set a timeout to close the connection after 5 seconds (for testing)
      this.visibilityTimeout = setTimeout(() => {
        console.log('Tab hidden for 5 seconds, closing connection for testing');
        this.closeConnection();
      }, 10 * 1000); // 10 seconds for testing (change back to 10 * 60 * 1000 for production)
    }
  }
  
  // Connect to WebSocket
  connect() {
    // Final check before creating a new socket instance
    if (!this.isAuthValid()) {
      console.error('WebSocket connection attempt failed: No valid session found.');
      return null;
    }

    // Reuse the existing instance if one already exists. Without this guard,
    // two concurrent callers (e.g. two hooks calling wsHandler in the same
    // tick when this.instance is null) each create a fresh Octopus and you
    // end up with two open WebSockets to the feed.
    if (this.instance) {
      // If the underlying socket is already up, do nothing. Otherwise let
      // Octopus.connect() decide — it has its own idempotency guard against
      // CONNECTING/OPEN sockets.
      if (this.instance.isConnected && this.instance.isConnected()) {
        return null;
      }
      return this.instance.connect();
    }

    // Create new instance with latest credentials
    this.instance = new Octopus({
      host: process.env.NODE_ENV === 'development' ? 'localhost:8585' : window.location.host,
      path: '/ws/v1/feeds',
      loginId: localStorage.getItem('login_id'),
      token: localStorage.getItem('auth_token')
    });

    return this.instance.connect();
  }

  // Close connection
  closeConnection() {
    if (this.instance) {
      try {
        console.log('OctopusManager: Performing full cleanup of instance');
        this.instance.destroy(); // This handles closing the socket AND stopping timers
        this.instance = null;
      } catch (err) {
        console.error('Error destroying Octopus instance:', err);
      }
    }
  }

  wsHandler(params) {
    if (!this.instance) {
      // If tab is visible and auth is valid, try to reconnect
      if (document.visibilityState === 'visible' && this.isAuthValid()) {
        console.log('Socket not connected, attempting to connect from wsHandler');
        this.connect();
      } else if (!this.isAuthValid()) {
        // Silently fail if not authenticated
        return null;
      }
    }
    return this.instance ? this.instance.wsHandler(params) : null;
  }
  
  // Check if connected
  isConnected() {
    return this.instance && this.instance.isConnected();
  }
  
  // Refresh all subscriptions to get fresh data
  refreshSubscriptions() {
    if (this.instance) {
      console.log('Refreshing all WebSocket subscriptions');
      return this.instance.refreshSubscriptions();
    }
    return false;
  }
  
  // Clean up resources
  cleanUp() {
    // Remove event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Clear any pending timeout
    if (this.visibilityTimeout) {
      clearTimeout(this.visibilityTimeout);
      this.visibilityTimeout = null;
    }
    
    // Close connection
    this.closeConnection();
  }
}

// Create a singleton instance
const octopusManager = new OctopusManager();

// Ensure cleanup on page unload
window.addEventListener('beforeunload', () => {
  octopusManager.cleanUp();
});

export default octopusManager;
